// Canonical primitive emit table — shared by Swift + Kotlin emitters.
//
// `@pyreon/primitives` exports the semantic JSX primitives that are the
// cross-platform UI vocabulary; this file maps each to its platform-native
// emit. The canonical set is `CANONICAL_PRIMITIVES` below — no count is
// repeated in prose here, because every hand-written count in this repo has
// rotted (15 vs 16 vs 17). `tests/canonical-primitives-exports.test.ts`
// asserts the Set equals the package's UI-primitive exports.
//
// ## What this file is
//
// - **Per-target tag name maps** — `<Stack>` → `VStack` on Swift / `Column` on Kotlin.
// - **Shared token-resolution helpers** — `padding={4}` → 16 (px/dp); `color="primary"`
//   → Swift `Color(...)` literal + Kotlin `Color(...)` literal. Single source
//   of truth so iOS + Android resolve the same canonical input to consistent
//   numeric/color values.
// - **`isCanonicalPrimitive` predicate** — the dispatchers in `emit-swift.ts` /
//   `emit-kotlin.ts` use this to decide whether to route through the canonical
//   emit functions before falling through to generic-tag emit.
//
// ## What this file is NOT
//
// - NOT per-target emit functions — those live in `emit-swift.ts` /
//   `emit-kotlin.ts` (one `emitSwiftStack` + one `emitKotlinStack`, etc.).
//   They consume the shared name maps + style helpers below.
// - NOT a JSX runtime — that's `@pyreon/primitives` (web side).
// - NOT a per-platform implementation — the iOS/Android emit GENERATES
//   the platform-native code; no runtime module is invoked.
//
// ## Scope
//
// EVERY member is wired end-to-end on both targets. Each has a dedicated
// per-target emit function except `Inline`, which deliberately shares
// `Stack`'s with a row default, since the two differ only in axis.
//
// Also exported by `@pyreon/primitives` but deliberately NOT members:
// `Transition` / `TransitionGroup` (animation wrappers), `WebView` (a native
// host for web content) and the `Web` / `NativeIOS` / `NativeAndroid` escape
// hatches. Each has its own dedicated emitter and never reaches generic emit,
// so the fell-through warning and the styled()/attrs()/rocketstyle() base
// check (the Set's consumers) do not apply to them.
//
// This comment said "6 primitives wired ... the other 10 fall through to
// generic emit" for long after that stopped being true, and it is the file the
// rest of the codebase treats as the single source of truth on the question —
// so the stale half was load-bearing misinformation, not a stale note. (It also
// listed nine names as ten.) When you add a primitive, update this block in the
// same change.

// ============================================================================
// Canonical primitive name set
// ============================================================================

/**
 * All canonical primitive tag names. Single source of truth — the dispatchers
 * use `isCanonicalPrimitive` to decide whether to route through canonical
 * emit before falling through to generic emit.
 *
 * Equals the UI-primitive exports of `@pyreon/primitives` (drift-locked by
 * `tests/canonical-primitives-exports.test.ts`). Every member routes through
 * a canonical per-target emit — dedicated, except `Inline` on `Stack`'s with
 * a row default.
 */
export const CANONICAL_PRIMITIVES = new Set([
  // Layout (5)
  'Stack',
  'Inline',
  'Layer',
  'Scroll',
  'Spacer',
  // Content (6)
  'Text',
  'Heading',
  'Image',
  'Audio',
  'Video',
  'Icon',
  // Interaction (3)
  'Button',
  'Press',
  'Link',
  // Input (3)
  'Field',
  'Toggle',
  'Modal',
])

export function isCanonicalPrimitive(tag: string): boolean {
  return CANONICAL_PRIMITIVES.has(tag)
}

// ============================================================================
// Per-target tag name maps
// ============================================================================

/**
 * Map canonical tag → Swift/SwiftUI View name. The Swift emit functions
 * (`emitSwiftStack`, `emitSwiftPress`, etc.) consult this table when
 * choosing the wrapper View. `<Stack direction="row">` switches at the
 * emit-function level to `HStack`.
 */
export const SWIFT_NAMES: Record<string, string> = {
  Stack: 'VStack', // direction="row" overrides to HStack at emit time
  Inline: 'HStack',
  Layer: 'ZStack',
  Scroll: 'ScrollView',
  Spacer: 'Spacer',
  Text: 'Text',
  Heading: 'Text', // .font(.largeTitle) at emit time
  Image: 'Image',
  Audio: 'PyreonAudioPlayer', // non-visual runtime player (AVFoundation engine)
  Video: 'PyreonVideoPlayer',
  Icon: 'Image', // systemName: from `name` prop
  Button: 'Button',
  Press: 'Button', // no chrome — emits the trailing-closure-only form
  Link: 'PyreonLink', // pushes onto the native router (@pyreon/native-router-swift)
  Field: 'TextField', // SecureField when kind="password"
  Toggle: 'Toggle',
  Modal: 'Sheet', // .sheet(isPresented:) modifier-based
}

/**
 * Map canonical tag → Kotlin/Compose Composable name.
 */
export const KOTLIN_NAMES: Record<string, string> = {
  Stack: 'Column', // direction="row" overrides to Row at emit time
  Inline: 'Row',
  Layer: 'Box',
  Scroll: 'Column', // verticalScroll modifier
  Spacer: 'Spacer',
  Text: 'Text',
  Heading: 'Text', // style=MaterialTheme.typography... at emit time
  Image: 'AsyncImage',
  Audio: 'PyreonAudioPlayer', // non-visual runtime player (Media3 engine)
  Video: 'PyreonVideoPlayer',
  Icon: 'Icon',
  Button: 'Button',
  Press: 'Box', // Modifier.clickable
  Link: 'PyreonLink', // wraps a Box(Modifier.clickable { navigate() }) (@pyreon/native-router-kotlin)
  Field: 'TextField',
  Toggle: 'Switch',
  Modal: 'Dialog',
}

// ============================================================================
// Token resolution — shared by Swift + Kotlin
// ============================================================================

/**
 * Resolve a canonical `padding` / `margin` / `gap` token to a pixel
 * (Swift CGFloat) / dp (Kotlin Dp) numeric value. The compiler emits
 * the integer; the platform converts to its native unit at runtime.
 *
 * - `0`-`9` → indexed 4px scale (0=0, 1=4, 2=8, ..., 9=48). Matches
 *   the web `@pyreon/primitives` token scale.
 * - `"xs"|"sm"|"md"|"lg"|"xl"` → semantic alias values (4/8/12/16/24).
 *
 * Unknown input returns 0 — defensive default; the canonical type-defs
 * constrain inputs to the allowed set so unknowns are catchable at the
 * TypeScript layer.
 */
export function resolveSpace(value: number | string): number {
  if (typeof value === 'number') {
    if (value >= 0 && value <= 9) return SPACE_BY_INDEX[value] ?? 0
    return 0
  }
  return SPACE_BY_NAME[value] ?? 0
}

const SPACE_BY_INDEX: Record<number, number> = {
  0: 0, 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 7: 32, 8: 40, 9: 48,
}

const SPACE_BY_NAME: Record<string, number> = {
  xs: 4, sm: 8, md: 12, lg: 16, xl: 24,
}

/**
 * Resolve a canonical color token to per-target color literal SOURCE
 * CODE. Returns the platform-native expression as a string ready to
 * splice into emit output.
 *
 * Web tokens are CSS hex strings (`#2563eb`). Native targets convert
 * to `Color(red:..., green:..., blue:...)` per-platform — same RGB,
 * just different syntax.
 *
 * Future arc: full theme integration — `Color.pyreonPrimary` extension
 * on Swift / `theme.colors.primary` on Compose. v1 keeps it inline +
 * literal so no runtime theme dependency.
 */
export function resolveColor(value: string, target: 'swift' | 'kotlin'): string {
  const rgb = COLOR_TOKENS_RGB[value]
  if (rgb === undefined) {
    // Unknown token — emit a neutral grey as a defensive fallback.
    return target === 'swift' ? 'Color.gray' : 'Color.Gray'
  }
  const [r, g, b] = rgb
  if (target === 'swift') {
    return `Color(red: ${r / 255}, green: ${g / 255}, blue: ${b / 255})`
  }
  // Kotlin / Compose Color hex: 0xFFRRGGBB. Emit bare `Color(...)`
  // matching the convention of @Serializable / Json / TextField / etc.
  // — consumer apps import from androidx.compose.ui.graphics.
  // Lets kotlin-stubs.ts ship the mock `Color` in the default package
  // without needing a multi-package stub file (Kotlin can't represent
  // that in one file).
  const hex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0').toUpperCase()
  return `Color(0xFF${hex})`
}

/**
 * RGB values for the default color palette. Mirrors
 * `packages/core/primitives/src/web/tokens.ts:COLOR_TOKENS`. Apps
 * with a custom theme will replace this in a future-arc PR.
 */
const COLOR_TOKENS_RGB: Record<string, [number, number, number]> = {
  text: [0x11, 0x18, 0x27], // gray-900 #111827
  surface: [0xff, 0xff, 0xff], // white
  primary: [0x25, 0x63, 0xeb], // blue-600 #2563eb
  secondary: [0x6b, 0x72, 0x80], // gray-500 #6b7280
  success: [0x16, 0xa3, 0x4a], // green-600 #16a34a
  warning: [0xd9, 0x77, 0x06], // amber-600 #d97706
  danger: [0xdc, 0x26, 0x26], // red-600 #dc2626
  muted: [0x9c, 0xa3, 0xaf], // gray-400 #9ca3af
}

/**
 * Resolve a canonical `radius` token to a platform-native numeric value.
 * Same scale as web (`packages/core/primitives/src/web/tokens.ts`).
 *
 * - `none` → 0
 * - `sm` → 4
 * - `md` → 8
 * - `lg` → 16
 * - `full` → 9999 (max practical pill / circle radius)
 */
export function resolveRadius(value: string): number {
  const map: Record<string, number> = { none: 0, sm: 4, md: 8, lg: 16, full: 9999 }
  return map[value] ?? 0
}

/**
 * Map canonical `align` to per-target alignment expression.
 *
 * Swift: `HorizontalAlignment` enum (.leading/.center/.trailing) for
 * VStack; `VerticalAlignment` for HStack. Caller passes axis-aware via
 * the `flavor` arg — VStack callers want horizontal alignment of the
 * column's children.
 *
 * Kotlin: `Alignment.Start/CenterHorizontally/End` for Column;
 * `Alignment.Top/CenterVertically/Bottom` for Row.
 */
export function resolveAlign(
  value: string,
  target: 'swift' | 'kotlin',
  flavor: 'horizontal' | 'vertical',
): string {
  if (target === 'swift') {
    if (flavor === 'horizontal') {
      return ALIGN_SWIFT_H[value] ?? '.leading'
    }
    return ALIGN_SWIFT_V[value] ?? '.top'
  }
  if (flavor === 'horizontal') {
    return ALIGN_KOTLIN_H[value] ?? 'Alignment.Start'
  }
  return ALIGN_KOTLIN_V[value] ?? 'Alignment.Top'
}

const ALIGN_SWIFT_H: Record<string, string> = {
  start: '.leading', center: '.center', end: '.trailing', stretch: '.leading',
}
const ALIGN_SWIFT_V: Record<string, string> = {
  start: '.top', center: '.center', end: '.bottom', stretch: '.top',
}
const ALIGN_KOTLIN_H: Record<string, string> = {
  start: 'Alignment.Start',
  center: 'Alignment.CenterHorizontally',
  end: 'Alignment.End',
  stretch: 'Alignment.Start', // Compose has no direct "stretch" for column children
}
const ALIGN_KOTLIN_V: Record<string, string> = {
  start: 'Alignment.Top',
  center: 'Alignment.CenterVertically',
  end: 'Alignment.Bottom',
  stretch: 'Alignment.Top',
}

/**
 * Canonical icon names → per-platform glyph identifiers (asset-pipeline
 * arc PR-1.3). ONE semantic name in shared source; each target renders
 * its native icon system:
 *
 *   - iOS:     SF Symbols (`Image(systemName: <sf>)`)
 *   - Android: Material Icons CORE set (`Icons.Filled.<material>` —
 *     compile-time references, so the host needs only the small
 *     `material-icons-core` artifact, never the huge -extended one)
 *   - Web:     the canonical name doubles as the app sprite's symbol id
 *
 * Curated to the intersection of material-icons-core's Filled set and
 * everyday UI needs (~36 glyphs: navigation, actions, status). An
 * UNMAPPED name warns and passes through raw on iOS (SF Symbols are
 * string-keyed, so direct SF ids keep working) and renders the
 * `warning` placeholder glyph on Android (visible, never silent).
 */
export const ICON_MAP: Record<string, { sf: string; material: string }> = {
  add: { sf: 'plus', material: 'Add' },
  'arrow-back': { sf: 'chevron.backward', material: 'ArrowBack' },
  'arrow-down': { sf: 'chevron.down', material: 'KeyboardArrowDown' },
  'arrow-forward': { sf: 'chevron.forward', material: 'ArrowForward' },
  'arrow-up': { sf: 'chevron.up', material: 'KeyboardArrowUp' },
  account: { sf: 'person.crop.circle', material: 'AccountCircle' },
  calendar: { sf: 'calendar', material: 'DateRange' },
  cart: { sf: 'cart.fill', material: 'ShoppingCart' },
  check: { sf: 'checkmark', material: 'Check' },
  'check-circle': { sf: 'checkmark.circle.fill', material: 'CheckCircle' },
  close: { sf: 'xmark', material: 'Close' },
  delete: { sf: 'trash', material: 'Delete' },
  edit: { sf: 'pencil', material: 'Edit' },
  email: { sf: 'envelope.fill', material: 'Email' },
  exit: { sf: 'rectangle.portrait.and.arrow.right', material: 'ExitToApp' },
  face: { sf: 'face.smiling', material: 'Face' },
  favorite: { sf: 'heart.fill', material: 'Favorite' },
  'favorite-outline': { sf: 'heart', material: 'FavoriteBorder' },
  home: { sf: 'house.fill', material: 'Home' },
  info: { sf: 'info.circle.fill', material: 'Info' },
  list: { sf: 'list.bullet', material: 'List' },
  location: { sf: 'mappin.circle.fill', material: 'LocationOn' },
  lock: { sf: 'lock.fill', material: 'Lock' },
  menu: { sf: 'line.3.horizontal', material: 'Menu' },
  more: { sf: 'ellipsis', material: 'MoreVert' },
  notifications: { sf: 'bell.fill', material: 'Notifications' },
  person: { sf: 'person.fill', material: 'Person' },
  phone: { sf: 'phone.fill', material: 'Phone' },
  play: { sf: 'play.fill', material: 'PlayArrow' },
  refresh: { sf: 'arrow.clockwise', material: 'Refresh' },
  search: { sf: 'magnifyingglass', material: 'Search' },
  send: { sf: 'paperplane.fill', material: 'Send' },
  settings: { sf: 'gearshape.fill', material: 'Settings' },
  share: { sf: 'square.and.arrow.up', material: 'Share' },
  star: { sf: 'star.fill', material: 'Star' },
  'thumb-up': { sf: 'hand.thumbsup.fill', material: 'ThumbUp' },
  warning: { sf: 'exclamationmark.triangle.fill', material: 'Warning' },
}

/**
 * `<Text size>` in points, mirroring the WEB impl's `SIZE_PX` exactly
 * (`packages/core/primitives/src/web/Text.tsx`). Same source, same rendered
 * size — a scale that drifts from the web's is a divergence that looks like a
 * design choice.
 */
export const TEXT_SIZE_PT: Record<string, number> = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 20,
  xl: 24,
}

/** `<Text weight>` per target. The web uses 400/500/700. */
export const TEXT_WEIGHT_SWIFT: Record<string, string> = {
  regular: '.regular',
  medium: '.medium',
  bold: '.bold',
}
export const TEXT_WEIGHT_KOTLIN: Record<string, string> = {
  regular: 'FontWeight.Normal',
  medium: 'FontWeight.Medium',
  bold: 'FontWeight.Bold',
}

/**
 * `<Field kind>` → the software keyboard each target should raise.
 *
 * The web gets this free from `<input type>`; native does not, and the
 * difference is a phone showing a full QWERTY where the same source shows a
 * numeric pad in a browser. `password` is absent on purpose: it selects the
 * MASKING path (SecureField / PasswordVisualTransformation), and a masked field
 * keeps the default keyboard on both platforms.
 */
export const FIELD_KEYBOARD_SWIFT: Record<string, string> = {
  number: '.numberPad',
  email: '.emailAddress',
  tel: '.phonePad',
  url: '.URL',
  search: '.webSearch',
}
export const FIELD_KEYBOARD_KOTLIN: Record<string, string> = {
  number: 'KeyboardType.Number',
  email: 'KeyboardType.Email',
  tel: 'KeyboardType.Phone',
  url: 'KeyboardType.Uri',
  search: 'KeyboardType.Text',
}
